import type { LucideIcon } from 'lucide-react';
import {
  Activity,
  AppWindow,
  Bell,
  Blocks,
  BookOpen,
  BookText,
  Bot,
  Box,
  Brain,
  Building,
  Building2,
  CircleCheck,
  Code2,
  Cpu,
  ClipboardList,
  DraftingCompass,
  FileText,
  FolderOpen,
  GitBranch,
  GraduationCap,
  History,
  Layers,
  LayoutGrid,
  List,
  Package,
  Puzzle,
  Rocket,
  Route,
  Rss,
  ScrollText,
  Server,
  Settings,
  Shapes,
  ShieldCheck,
  Sparkles,
  Workflow,
  UserCog,
  Wrench,
} from 'lucide-react';

// The sidebar as data: the same items, order, groups and gates as the old
// Layout.jsx (Flow and Benchmark dropped). Rendering lives in shell/Sidebar.

export type BadgeKey = 'alerts' | 'board';

export interface NavItem {
  kind: 'item';
  title: string;
  /** The project page this item opens (a PROJECT_ROUTES path). */
  page: string;
  icon: LucideIcon;
  badge?: BadgeKey;
  /** Informational count (white/20) instead of the red alarm badge. */
  badgeNeutral?: boolean;
  /** data-tour anchor; defaults to nav-<page>. */
  tour?: string;
}

export interface NavGroup {
  kind: 'group';
  title: string;
  icon: LucideIcon;
  /** Items, or sub-groups (Modules → Data Governance). */
  items: (NavItem | NavGroup)[];
}

export type NavEntry = NavItem | NavGroup | { kind: 'separator' } | { kind: 'search' } | { kind: 'tutorial' };

export type UiView = 'simple' | 'advanced';

/** What the gates read about the caller (from GET /api/me). */
export interface NavCaller {
  roles: string[];
}

const item = (title: string, page: string, icon: LucideIcon, extra: Partial<NavItem> = {}): NavItem => ({
  kind: 'item',
  title,
  page,
  icon,
  ...extra,
});

export const ITEMS = {
  dashboard: item('Dashboard', 'dashboard', LayoutGrid),
  alerts: item('Alerts', 'alerts', Bell, { badge: 'alerts' }),
  board: item('Board', 'board', ClipboardList, { badge: 'board', badgeNeutral: true, tour: 'my-requests' }),
  feed: item('Feed', 'feed', Rss),
  apps: item('Apps', 'apps', AppWindow),
  agents: item('Agents', 'agents', Bot),
  docs: item('Docs', 'docs', BookText),
  guides: item('Guides', 'guides', GraduationCap),
  access: item('Access', 'access', UserCog),
  models: item('Models', 'models', Brain),
  reports: item('Reports', 'reports', FileText),
  workflows: item('Workflows', 'workflows', Route),
  pipeline: item('Data', 'pipeline', Workflow),
  structure: item('Structure', 'structure', Layers),
  deployments: item('Deployments', 'deployments', Rocket),
  status: item('Status', 'status', Activity),
  code: item('Code', 'code', Code2),
  branches: item('Branches', 'branches', GitBranch),
  versions: item('Versions', 'versions', History),
  auditLog: item('Audit Log', 'audit-log', ScrollText),
  audit: item('Audit', 'audit', ShieldCheck),
  projects: item('Projects', 'projects', FolderOpen),
  organizations: item('Organizations', 'organizations', Building2),
  fleet: item('Agent fleet', 'fleet', Server),
  aiGateway: item('AI gateway', 'ai-gateway', Cpu),
  codeCity: item('Code City', 'code-city', Building),
  ai: item('AI', 'settings/ai', Sparkles, { tour: 'nav-ai' }),
  templates: item('Templates', 'templates', Package),
  cortexAudit: item('Cortex Audit', 'cortex-audit', ShieldCheck),
  settings: item('Settings', 'settings', Settings),
} as const;

/** The Data Governance group: the data-dictionary workflow, in reading order. */
export const DATA_GOVERNANCE_ITEMS: NavItem[] = [
  item('Overview', 'data-dictionary', BookOpen),
  item('Elements', 'data-elements', Blocks),
  item('Sources', 'data-sources', Box),
  item('Access Policies', 'access-policies', ShieldCheck),
  item('Source Mappings', 'source-mappings', GitBranch),
  item('Reference Lists', 'reference-lists', List),
  item('Processing Rules', 'processing-rules', Puzzle),
  item('Data Review', 'data-review', DraftingCompass),
  item('Quality', 'dictionary-quality', CircleCheck),
];

const has = (caller: NavCaller, ...roles: string[]) => caller.roles.some((r) => roles.includes(r));

/**
 * Presentation gates, as on the old platform (the server stays the enforcement point):
 * Organizations for org and platform admins; Feed and Templates (the app templates given to the
 * organization, ADR 0018 §2) for org members; the internal surfaces (Agent fleet, Cortex Audit) for
 * platform admins — the old "@mindshine.com staff" email check becomes the platform_admin role.
 */
export function navGates(caller: NavCaller) {
  const platformAdmin = has(caller, 'platform_admin');
  return {
    organizations: has(caller, 'platform_admin', 'org_admin'),
    auditLog: has(caller, 'platform_admin', 'org_admin'),
    feed: has(caller, 'platform_admin', 'org_admin', 'member'),
    templates: has(caller, 'platform_admin', 'org_admin', 'member'),
    fleet: platformAdmin,
    aiGateway: platformAdmin,
    codeCity: platformAdmin,
    cortexAudit: platformAdmin,
  };
}

export function buildNav(view: UiView, caller: NavCaller): NavEntry[] {
  const gate = navGates(caller);
  if (view === 'simple') {
    // Standard mode: the everyday surfaces only. Everything else stays reachable by URL.
    return [
      { kind: 'search' },
      ITEMS.apps,
      ITEMS.agents,
      ITEMS.guides,
      ITEMS.board,
      ITEMS.workflows,
      ITEMS.docs,
      ITEMS.status,
      ...(gate.feed ? [ITEMS.feed] : []),
    ];
  }
  return [
    { kind: 'search' },
    ITEMS.dashboard,
    ITEMS.alerts,
    ITEMS.board,
    ...(gate.feed ? [ITEMS.feed] : []),
    { kind: 'separator' },
    ITEMS.apps,
    ITEMS.docs,
    {
      kind: 'group',
      title: 'Modules',
      icon: Shapes,
      items: [
        ITEMS.agents,
        ITEMS.pipeline,
        { kind: 'group', title: 'Data Governance', icon: Puzzle, items: DATA_GOVERNANCE_ITEMS },
        ITEMS.guides,
        ITEMS.access,
        ITEMS.models,
        ITEMS.reports,
        ITEMS.workflows,
      ],
    },
    {
      kind: 'group',
      title: 'Operations',
      icon: Wrench,
      items: [
        ITEMS.structure,
        ITEMS.deployments,
        ITEMS.ai,
        ITEMS.status,
        ITEMS.code,
        // The project's code as a 3D city (ADR 0042): platform admins only for now (members: an owner decision).
        ...(gate.codeCity ? [ITEMS.codeCity] : []),
        ITEMS.branches,
        ITEMS.versions,
        ITEMS.audit,
      ],
    },
    { kind: 'separator' },
    {
      kind: 'group',
      title: 'Admin',
      icon: Building2,
      items: [
        ITEMS.projects,
        ...(gate.organizations ? [ITEMS.organizations] : []),
        ...(gate.auditLog ? [ITEMS.auditLog] : []),
        ...(gate.fleet ? [ITEMS.fleet] : []),
        ...(gate.aiGateway ? [ITEMS.aiGateway] : []),
        ...(gate.codeCity ? [ITEMS.codeCity] : []),
        ...(gate.templates ? [ITEMS.templates] : []),
        ...(gate.cortexAudit ? [ITEMS.cortexAudit] : []),
      ],
    },
    { kind: 'separator' },
    ITEMS.settings,
    { kind: 'tutorial' },
  ];
}

/** Every item a nav contains, groups flattened, in order. */
export function flattenNav(entries: NavEntry[]): NavItem[] {
  return entries.flatMap((e) => (e.kind === 'item' ? [e] : e.kind === 'group' ? flattenNav(e.items) : []));
}

/** Whether any item of a group (its sub-groups included) is the active page. */
export function isGroupActive(group: NavGroup, rest: string): boolean {
  return group.items.some((it) =>
    it.kind === 'group' ? isGroupActive(it, rest) : isItemActive(it.page, rest),
  );
}

/** Pages whose sub-routes keep the item highlighted (apps/:viewName, agents/:agentId). */
const PREFIX_ACTIVE = new Set(['apps', 'agents', 'code']);

/** Whether a nav item is the active page for `rest` (the path after the project prefix). */
export function isItemActive(page: string, rest: string): boolean {
  if (rest === page) return true;
  if (rest === '' && page === 'dashboard') return true;
  return PREFIX_ACTIVE.has(page) && rest.startsWith(`${page}/`);
}
